import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { billAPI } from '../services/api';
import { Loader2, DollarSign } from 'lucide-react';
import { Badge } from './ui/badge';

const PaymentHistoryModal = ({ orderId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHistory = async () => {
            if (!orderId) return;
            try {
                setLoading(true);
                const response = await billAPI.getPaymentHistory(orderId);
                // Assume response.data is the list or response.data.content
                const data = response.data.content || response.data || [];
                // If the API returns the Order DTO which contains history? 
                // Or a specific list DTO? 
                // Using generic approach, assume list.
                setTransactions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Failed to fetch payment history:', err);
                setError('Failed to load payment history.');
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [orderId]);

    const getStatusVariant = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUCCESS': return 'default'; // Greenish usually if default is configured that way, else 'success' logic needed
            case 'COMPLETED': return 'default';
            case 'FAILED': return 'destructive';
            case 'PENDING': return 'secondary';
            default: return 'outline';
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Payment History</DialogTitle>
                    <DialogDescription>
                        Transaction details for Order #{orderId}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Loader2 className="h-8 w-8 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-destructive p-4">
                            {error}
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="text-center text-muted-foreground p-8">
                            No payment transactions found for this order.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Reference Ref</TableHead>
                                    <TableHead>Method</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {transactions.map((txn, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{txn.paymentDate || txn.paymentAddDate || txn.paymentDateTime || txn.date || txn.createdAt?.split('T')[0]}</TableCell>
                                        <TableCell>{txn.transactionReferenceNumber || txn.referenceNumber || '-'}</TableCell>
                                        <TableCell>{txn.paymentMethod}</TableCell>
                                        <TableCell>Rs {(txn.amount || txn.amountPaid) ? parseFloat(txn.amount || txn.amountPaid).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell>
                                            <Badge variant={getStatusVariant(txn.paymentStatus || txn.status)}>
                                                {txn.paymentStatus || txn.status || 'Unknown'}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentHistoryModal;
